/**
 * Checklist Template Section
 * Displays and manages the checklist template attached to an installation job.
 * Similar UX to SensorManagementSection.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, Plus, X, CheckSquare, Wrench, Package, FileText, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTemplates, useTemplate, useUpdateInstallationJob } from '../hooks/useInstallationJobs';
import type { InstallationJob, InstallationJobTemplate } from '../types';

interface ChecklistTemplateSectionProps {
  job: InstallationJob;
}

export function ChecklistTemplateSection({ job }: ChecklistTemplateSectionProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'apply' | 'change' | 'remove' | null>(null);

  const { data: templatesResponse, isLoading: isLoadingTemplates } = useTemplates();
  const { data: currentTemplate } = useTemplate(job.templateId);
  const updateJob = useUpdateInstallationJob();

  const templates = templatesResponse?.templates?.filter((t) => t.isActive) ?? [];
  const hasExistingResponses = job.checklist.some((item) => item.completedAt);

  const applyTemplate = async (template: InstallationJobTemplate) => {
    try {
      await updateJob.mutateAsync({
        id: job.id,
        input: {
          templateId: template.id,
          checklist: template.checklistItems.map((item, index) => ({
            ...item,
            id: crypto.randomUUID(),
            sortOrder: item.sortOrder ?? index,
          })),
          toolItems: template.toolItems.map((item, index) => ({
            ...item,
            id: crypto.randomUUID(),
            confirmed: false,
            sortOrder: item.sortOrder ?? index,
          })),
          materialItems: template.materialItems.map((item, index) => ({
            ...item,
            id: crypto.randomUUID(),
            confirmed: false,
            sortOrder: item.sortOrder ?? index,
          })),
          termsAndConditions: template.termsAndConditions.map((item, index) => ({
            ...item,
            id: crypto.randomUUID(),
            acknowledged: false,
            sortOrder: item.sortOrder ?? index,
          })),
        },
      });
      toast.success(`Template "${template.name}" applied`);
      setSelectedTemplateId('');
    } catch {
      toast.error('Failed to apply template');
    }
  };

  const removeTemplate = async () => {
    try {
      await updateJob.mutateAsync({
        id: job.id,
        input: {
          templateId: undefined,
          checklist: [],
          toolItems: [],
          materialItems: [],
          termsAndConditions: [],
        },
      });
      toast.success('Template removed');
    } catch {
      toast.error('Failed to remove template');
    }
  };

  const handleApply = () => {
    if (!selectedTemplateId) return;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return;

    if (job.templateId && (job.checklist.length > 0 || hasExistingResponses)) {
      setPendingAction('change');
      setIsConfirmDialogOpen(true);
    } else {
      applyTemplate(template);
    }
  };

  const handleRemove = () => {
    if (hasExistingResponses) {
      setPendingAction('remove');
      setIsConfirmDialogOpen(true);
    } else {
      removeTemplate();
    }
  };

  const handleConfirm = () => {
    setIsConfirmDialogOpen(false);
    if (pendingAction === 'change') {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) applyTemplate(template);
    } else if (pendingAction === 'remove') {
      removeTemplate();
    }
    setPendingAction(null);
  };

  const itemCounts = [
    { icon: CheckSquare, label: 'Checklist', count: job.checklist.length },
    { icon: Wrench, label: 'Tools', count: job.toolItems.length },
    { icon: Package, label: 'Materials', count: job.materialItems.length },
    { icon: FileText, label: 'Terms', count: job.termsAndConditions.length },
  ];

  const completedChecklist = job.checklist.filter((item) => item.completedAt).length;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Checklist Template
            {job.templateId && job.checklist.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {completedChecklist}/{job.checklist.length}
              </Badge>
            )}
          </CardTitle>
          {job.templateId && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={handleRemove}
              disabled={updateJob.isPending}
              title="Remove template"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {job.templateId && currentTemplate ? (
            <div className="space-y-3">
              {/* Current template info */}
              <div>
                <p className="font-medium text-sm">{currentTemplate.name}</p>
                {currentTemplate.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{currentTemplate.description}</p>
                )}
              </div>

              {/* Item counts */}
              <div className="grid grid-cols-2 gap-2">
                {itemCounts.map(({ icon: Icon, label, count }) =>
                  count > 0 ? (
                    <div key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" />
                      <span>
                        {count} {label}
                      </span>
                    </div>
                  ) : null,
                )}
              </div>

              {/* Progress for checklist items */}
              {job.checklist.length > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Checklist Progress</span>
                    <span>
                      {completedChecklist}/{job.checklist.length}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full bg-primary transition-all"
                      style={{ width: `${(completedChecklist / job.checklist.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Change template */}
              <div className="pt-1">
                <div className="flex items-center gap-2">
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Change template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates
                        .filter((t) => t.id !== job.templateId)
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplateId && (
                    <Button size="sm" variant="outline" className="h-8" onClick={handleApply} disabled={updateJob.isPending}>
                      Apply
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : job.templateId ? (
            // Template ID exists but template data still loading
            <div className="space-y-2">
              <div className="h-4 w-32 rounded bg-muted animate-pulse" />
              <div className="h-3 w-48 rounded bg-muted animate-pulse" />
            </div>
          ) : (
            // No template applied
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                No checklist template applied. Select a template to add checklist items, tools, materials, and terms.
              </p>
              {isLoadingTemplates ? (
                <div className="h-8 w-full rounded bg-muted animate-pulse" />
              ) : templates.length === 0 ? (
                <p className="text-xs text-muted-foreground">No active templates available.</p>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                          <span className="text-muted-foreground ml-2">
                            ({t.checklistItems.length} items)
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplateId && (
                    <Button size="sm" onClick={handleApply} disabled={updateJob.isPending}>
                      <Plus className="mr-1 h-4 w-4" />
                      Apply
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {pendingAction === 'remove' ? 'Remove Template' : 'Change Template'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {hasExistingResponses ? (
                <>
                  This job has checklist items with technician responses. {pendingAction === 'remove' ? 'Removing' : 'Changing'} the
                  template will <strong>delete all existing checklist items, tools, materials, and terms</strong> including
                  completed responses. This cannot be undone.
                </>
              ) : (
                <>
                  This will replace the current checklist items, tools, materials, and terms with those from the new template.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {pendingAction === 'remove' ? 'Remove' : 'Replace'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
